import React, { useState, useEffect, useRef } from 'react';
import { Button } from '@mentimeter/ragnar-ui/button';
import { Text } from '@mentimeter/ragnar-ui/text';
import {
  PopoverRoot,
  PopoverContainer,
  PopoverBody,
  PopoverAnchor,
} from '@mentimeter/ragnar-ui/popover';
import { Box } from '@mentimeter/ragnar-ui/box';
import { clsx } from '@mentimeter/ragnar-tailwind-config';
import { whiteBase, gray600 } from '@mentimeter/ragnar-colors';
import { TOUR_LENGTH } from '../../hooks/use-tour-store';
import { TOUR_ELEMENT_SHOW_DELAY } from '../../constants';
import './tour-popover-animate.css';
import { ProgressBar } from '../ProgressBar';

export interface TourPopoverProps {
  shouldShowPopover: boolean;
  currentStep: number;
  totalSteps?: number;
  title: string;
  description: string;
  onNext?: (() => void) | undefined;
  side?: 'top' | 'right' | 'bottom' | 'left';
  arrowIllustration?: React.ReactNode;
  align?: 'start' | 'center' | 'end';
  triggerRef: React.RefObject<HTMLElement>;
  animate?: boolean;
  sideOffset?: number;
}

export const TourPopover: React.FC<TourPopoverProps> = ({
  shouldShowPopover,
  currentStep,
  totalSteps = TOUR_LENGTH,
  title,
  description,
  onNext,
  sideOffset = 0,
  side = 'right',
  align = 'center',
  arrowIllustration,
  triggerRef,
  animate = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    // Clear any existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    if (shouldShowPopover) {
      const showPopover = () => {
        // Check if target element exists and has dimensions
        const targetElement = triggerRef.current;
        if (targetElement) {
          const rect = targetElement.getBoundingClientRect();
          // Only show if element has stable dimensions
          if (rect.width > 0 && rect.height > 0) {
            setIsOpen(true);
            return;
          }
        }

        // If element doesn't have dimensions yet, wait a bit more and try again
        timeoutRef.current = setTimeout(() => {
          setIsOpen(true); // Show anyway after additional delay
        }, 500); // Additional 500ms wait
      };

      // Initial delay to allow UI to settle before checking
      timeoutRef.current = setTimeout(showPopover, TOUR_ELEMENT_SHOW_DELAY);
    } else {
      // Hide immediately when shouldShowPopover becomes false
      setIsOpen(false);
    }

    // Cleanup function
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, [shouldShowPopover, triggerRef]);
  return (
    <PopoverRoot open={isOpen}>
      <PopoverAnchor virtualRef={triggerRef} />
      <PopoverContainer
        // Figure out how to style the arrow change size and color, decide if we want to invest time in this, or rather hide it
        showArrow={true}
        borderRadius="xl"
        align={align}
        side={side}
        width={300}
        height="100%"
        sideOffset={sideOffset}
        className={clsx(
          'dark',
          'tour-popover-container',
          animate && 'tour-popover-animation',
        )}
      >
        <PopoverBody
          px="space6"
          py="space8"
          m="space0"
          height="100%"
          flexDirection="column"
          justifyContent="space-between"
        >
          <Box
            flex="1"
            justifyContent="center"
            alignItems="center"
            mb="space8"
            mt="space4"
          >
            {arrowIllustration}
          </Box>
          <Box flexDirection="column" gap="space4">
            <Text color={whiteBase} variant="contentHero4" fontSize="300">
              {title}
            </Text>
            <Text color={gray600} variant="editorBodyMd">
              {description}
            </Text>
          </Box>
          <Box
            width="100%"
            justifyContent="space-between"
            flexDirection="row"
            alignItems="center"
            pt="space6"
          >
            <Box width="50%">
              <ProgressBar step={currentStep} totalSteps={totalSteps} />
            </Box>
            <Button variant="primary" onClick={onNext}>
              {/* Add i18n */}
              Next
            </Button>
          </Box>
        </PopoverBody>
      </PopoverContainer>
    </PopoverRoot>
  );
};
